import type { ComponentProps } from 'react';
import { useState } from 'react';
import MDEditor, { commands } from '@uiw/react-md-editor';
import classNames from '../../../../utils/classNames';
import { toStringOrEmpty } from '../../../../utils/cast';
import { MarkdownPreview } from '../../../../utils/markdown';
import '@uiw/react-md-editor/markdown-editor.css';
import { useMarkdownEditorAutoHeight } from './use-markdown-editor-auto-height';

type MarkdownEditorProps = {
    name: string;
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    isReadOnly?: boolean;
    isDisabled?: boolean;
};

type EditorCommandList = NonNullable<ComponentProps<typeof MDEditor>['commands']>;
type EditorCommand = EditorCommandList[number];
type MarkdownCommandKey =
    | 'bold'
    | 'italic'
    | 'link'
    | 'quote'
    | 'unorderedListCommand'
    | 'orderedListCommand'
    | 'divider'
    | 'title';

// uiw exposes command metadata with `any` types; we narrow this locally.
// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
const editorCommands = commands as Partial<Record<MarkdownCommandKey, EditorCommand>>;
function getEditorCommandOrThrow(commandKey: MarkdownCommandKey): EditorCommand {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const command = editorCommands[commandKey];
    if (!command) {
        throw new Error(`Missing markdown editor command "${commandKey}".`);
    }

    return command;
}

function collapseSelectionToEnd(command: EditorCommand): EditorCommand {
    const originalExecute = command.execute;

    return {
        ...command,
        execute(state, api, dispatch, executeCommandState, shortcuts) {
            originalExecute?.(state, api, dispatch, executeCommandState, shortcuts);

            const selectionEnd = Math.max(api.textArea.selectionStart, api.textArea.selectionEnd);
            api.setSelectionRange({ start: selectionEnd, end: selectionEnd });
        },
    };
}

const quoteCommand = collapseSelectionToEnd(getEditorCommandOrThrow('quote'));
const unorderedListCommand = collapseSelectionToEnd(
    getEditorCommandOrThrow('unorderedListCommand')
);
const orderedListCommand = collapseSelectionToEnd(getEditorCommandOrThrow('orderedListCommand'));

const markdownCommands: EditorCommandList = [
    getEditorCommandOrThrow('bold'),
    getEditorCommandOrThrow('italic'),
    getEditorCommandOrThrow('link'),
    quoteCommand,
    unorderedListCommand,
    orderedListCommand,
    getEditorCommandOrThrow('divider'),
    getEditorCommandOrThrow('title'),
];

export function MarkdownEditor({
    name,
    value,
    onChange,
    placeholder,
    isReadOnly = false,
    isDisabled = false,
}: MarkdownEditorProps): JSX.Element {
    const [showPreview, setShowPreview] = useState(false);
    const { editorHeight, editorRootRef, handleTextareaChange } = useMarkdownEditorAutoHeight({
        value,
        isDisabled: isReadOnly,
    });

    return (
        <div
            ref={editorRootRef}
            className={classNames(
                'markdown-field flex w-full flex-col gap-2',
                (isReadOnly || isDisabled) && '-disabled'
            )}
            data-color-mode="light"
            data-field-name={name}
        >
            {isReadOnly ? (
                <div className="markdown-field__preview rounded border border-neutral-300 bg-neutral-100">
                    <MarkdownPreview markdownText={value} />
                </div>
            ) : (
                <fieldset
                    disabled={isDisabled}
                    className="flex min-w-0 flex-col gap-2 border-0 p-0"
                >
                    <div className="flex items-center justify-end">
                        <button
                            type="button"
                            className="rounded border border-neutral-300 bg-white px-3 py-1 text-sm text-neutral-700 hover:bg-neutral-50"
                            onClick={() => {
                                setShowPreview((currentValue) => !currentValue);
                            }}
                        >
                            {showPreview
                                ? gettext('HIDE_RICH_TEXT_PREVIEW')
                                : gettext('SHOW_RICH_TEXT_PREVIEW')}
                        </button>
                    </div>
                    <MDEditor
                        value={value}
                        onChange={(nextValue: string | undefined) => {
                            onChange(toStringOrEmpty(nextValue));
                        }}
                        commands={
                            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                            markdownCommands
                        }
                        extraCommands={[]}
                        preview="edit"
                        visibleDragbar={false}
                        height={editorHeight}
                        textareaProps={{
                            id: `id_${name}`,
                            placeholder: placeholder ?? gettext('MARKDOWN_EDITOR_PLACEHOLDER'),
                            'aria-labelledby': `form-field-label-${name}`,
                            disabled: isDisabled,
                            onChange: handleTextareaChange,
                        }}
                    />
                    {showPreview && (
                        <div className="markdown-field__preview rounded border border-neutral-300 bg-white">
                            <MarkdownPreview markdownText={value} />
                        </div>
                    )}
                </fieldset>
            )}
        </div>
    );
}
